import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Users, Eye, XCircle, CheckCircle, RefreshCw, Clock, Globe, Smartphone, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const SecurityDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [riskFilter, setRiskFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [riskFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, usersRes] = await Promise.all([
        api.get('/admin/security/overview'),
        api.get(`/admin/security/users?risk_filter=${riskFilter}`)
      ]);
      setOverview(overviewRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await api.get(`/admin/security/users/${userId}/details`);
      setUserDetails(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const runFraudCheck = async () => {
    setRefreshing(true);
    try {
      await api.post('/admin/security/run-fraud-check');
      await fetchData();
      alert('✅ Analyse anti-fraude terminée avec succès !');
    } catch (error) {
      console.error('Error running fraud check:', error);
      alert('❌ Erreur lors de l\'analyse');
    } finally {
      setRefreshing(false);
    }
  };

  const updateAdminDecision = async (userId, decision, notes) => {
    try {
      await api.put(`/admin/security/users/${userId}/decision`, {
        decision,
        admin_notes: notes
      });
      setShowModal(false);
      await fetchData();
      alert('✅ Décision enregistrée avec succès !');
    } catch (error) {
      console.error('Error updating decision:', error);
      alert('❌ Erreur lors de la mise à jour');
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high':
        return 'text-[#FF3B5C] bg-[#FF3B5C]/10 border-[#FF3B5C]';
      case 'suspect':
        return 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]';
      default:
        return 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]';
    }
  };

  const getRiskEmoji = (risk) => {
    switch (risk) {
      case 'high': return '🔴';
      case 'suspect': return '🟡';
      default: return '🟢';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00FFFF]"></div>
        <p className="text-[#B8B8BA] mt-4">Chargement des données de sécurité...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-8 rounded-lg metallic-border cyber-glow"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-[#00FFFF]" />
            <h2 className="text-3xl font-bold text-white">Sécurité & Anti-Fraude</h2>
          </div>
          <button
            onClick={runFraudCheck}
            disabled={refreshing}
            className="btn-primary py-2 px-4 text-sm flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Analyse...' : 'Lancer Analyse'}</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#222226] p-6 rounded-lg text-center">
            <Users className="h-8 w-8 text-[#00FFFF] mx-auto mb-2" />
            <div className="text-3xl font-bold text-white mb-1">{overview?.total_users || 0}</div>
            <div className="text-[#B8B8BA] text-sm">Total Utilisateurs</div>
          </div>
          
          <div className="bg-[#222226] p-6 rounded-lg text-center border-l-4 border-[#00FF88]">
            <div className="text-3xl font-bold text-[#00FF88] mb-1">{overview?.safe || 0}</div>
            <div className="text-[#B8B8BA] text-sm">🟢 Sûrs</div>
          </div>
          
          <div className="bg-[#222226] p-6 rounded-lg text-center border-l-4 border-[#FFB800]">
            <div className="text-3xl font-bold text-[#FFB800] mb-1">{overview?.suspect || 0}</div>
            <div className="text-[#B8B8BA] text-sm">🟡 Suspects</div>
          </div>
          
          <div className="bg-[#222226] p-6 rounded-lg text-center border-l-4 border-[#FF3B5C]">
            <div className="text-3xl font-bold text-[#FF3B5C] mb-1">{overview?.high_risk || 0}</div>
            <div className="text-[#B8B8BA] text-sm">🔴 High Risk</div>
          </div>
        </div>
      </motion.div>

      {/* Filtres */}
      <div className="flex space-x-2">
        {['all', 'high', 'suspect', 'safe'].map((filter) => (
          <button
            key={filter}
            onClick={() => setRiskFilter(filter)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              riskFilter === filter
                ? 'bg-[#00FFFF] text-black'
                : 'bg-[#222226] text-[#B8B8BA] hover:bg-[#2A2A2E]'
            }`}
          >
            {filter === 'all' ? 'Tous' : filter === 'high' ? 'High Risk' : filter === 'suspect' ? 'Suspects' : 'Sûrs'}
          </button>
        ))}
      </div>

      {/* Liste des Utilisateurs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#1A1A1D] p-6 rounded-lg metallic-border"
      >
        <h3 className="text-xl font-bold text-white mb-4">Utilisateurs ({users.length})</h3>
        
        {users.length === 0 ? (
          <div className="text-center py-8 text-[#B8B8BA]">
            Aucun utilisateur dans cette catégorie
          </div>
        ) : (
          <div className="space-y-2">
            {users.map((user) => (
              <div
                key={user.id}
                className="bg-[#222226] p-4 rounded-lg flex items-center justify-between hover:bg-[#2A2A2E] transition-colors cursor-pointer"
                onClick={() => fetchUserDetails(user.id)}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className="text-2xl">{getRiskEmoji(user.risk_level)}</div>
                  <div className="flex-1">
                    <div className="text-white font-medium">{user.email}</div>
                    <div className="text-[#6B6B6E] text-sm">
                      {user.full_name} • {user.role} • {user.session_count} sessions
                    </div>
                    {user.flags && user.flags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.flags.map((flag, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-[#FF3B5C]/20 text-[#FF3B5C] text-xs rounded">
                            {flag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-sm text-[#B8B8BA]">
                    <div>IP: {user.last_seen_ip || 'N/A'}</div>
                    <div className="text-xs">
                      {user.last_seen_at ? new Date(user.last_seen_at).toLocaleString('fr-FR') : 'N/A'}
                    </div>
                  </div>
                </div>
                <Eye className="h-5 w-5 text-[#00FFFF] ml-4" />
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Modal Détails Utilisateur */}
      <AnimatePresence>
        {showModal && userDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#1A1A1D] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto metallic-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                    <Shield className="h-6 w-6 text-[#00FFFF]" />
                    <span>Détails Sécurité</span>
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-[#B8B8BA] hover:text-white">
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>

                {/* User Info */}
                <div className="bg-[#222226] p-4 rounded-lg mb-4">
                  <div className="text-white font-bold text-lg mb-2">{userDetails.user.email}</div>
                  <div className="text-[#B8B8BA] text-sm space-y-1">
                    <div>👤 {userDetails.user.full_name}</div>
                    <div>🎭 {userDetails.user.role}</div>
                    <div>📅 Créé le {new Date(userDetails.user.created_at).toLocaleString('fr-FR')}</div>
                    <div>Status: {userDetails.user.is_active ? '✅ Actif' : '❌ Désactivé'}</div>
                  </div>
                </div>

                {/* Fraud Status */}
                <div className="bg-[#222226] p-4 rounded-lg mb-4">
                  <h3 className="text-white font-bold mb-2">🛡️ Statut Anti-Fraude</h3>
                  <div className="space-y-2">
                    <div className={`inline-block px-3 py-1 rounded-full text-sm font-bold border ${getRiskColor(userDetails.fraud_status.risk_level)}`}>
                      {getRiskEmoji(userDetails.fraud_status.risk_level)} {userDetails.fraud_status.risk_level?.toUpperCase()}
                    </div>
                    {userDetails.fraud_status.flags && userDetails.fraud_status.flags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {userDetails.fraud_status.flags.map((flag, idx) => (
                          <span key={idx} className="px-2 py-1 bg-[#FF3B5C]/20 text-[#FF3B5C] text-xs rounded">
                            ⚠️ {flag}
                          </span>
                        ))}
                      </div>
                    )}
                    {userDetails.fraud_status.detection_reason && (
                      <div className="text-[#B8B8BA] text-sm mt-2">
                        📋 {userDetails.fraud_status.detection_reason}
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Data */}
                <div className="bg-[#222226] p-4 rounded-lg mb-4">
                  <h3 className="text-white font-bold mb-3">🔐 Données de Sécurité</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-[#00FFFF] text-sm mb-1 flex items-center space-x-1">
                        <Globe className="h-4 w-4" />
                        <span>Adresses IP ({userDetails.security_data.ip_addresses?.length || 0})</span>
                      </div>
                      <div className="text-[#B8B8BA] text-sm">
                        {userDetails.security_data.ip_addresses?.join(', ') || 'Aucune'}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-[#00FFFF] text-sm mb-1 flex items-center space-x-1">
                        <Smartphone className="h-4 w-4" />
                        <span>Device Tokens ({userDetails.security_data.device_tokens?.length || 0})</span>
                      </div>
                      <div className="text-[#B8B8BA] text-sm break-all">
                        {userDetails.security_data.device_tokens?.map(t => t.substring(0, 30) + '...').join(', ') || 'Aucun'}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-[#00FFFF] text-sm mb-1 flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>Sessions</span>
                      </div>
                      <div className="text-[#B8B8BA] text-sm">
                        {userDetails.security_data.session_count} sessions • Dernière: {new Date(userDetails.security_data.last_seen_at).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cartes de Paiement */}
                {userDetails.user.payment_methods_used && userDetails.user.payment_methods_used.length > 0 && (
                  <div className="bg-[#222226] p-4 rounded-lg mb-4">
                    <h3 className="text-white font-bold mb-3">💳 Cartes Utilisées</h3>
                    <div className="flex flex-wrap gap-2">
                      {userDetails.user.payment_methods_used.map((card, idx) => (
                        <div key={idx} className="bg-[#1A1A1D] px-3 py-2 rounded text-sm text-white">
                          **** **** **** {card}
                        </div>
                      ))}
                    </div>
                    {userDetails.user.last_payment_info && (
                      <div className="text-[#B8B8BA] text-xs mt-2">
                        Dernier paiement: {new Date(userDetails.user.last_payment_info.last_payment_date).toLocaleString('fr-FR')}
                      </div>
                    )}
                  </div>
                )}

                {/* Comptes Liés */}
                {userDetails.linked_accounts && userDetails.linked_accounts.length > 0 && (
                  <div className="bg-[#222226] p-4 rounded-lg mb-4">
                    <h3 className="text-white font-bold mb-3">⚠️ Comptes Liés ({userDetails.linked_accounts.length})</h3>
                    <div className="space-y-2">
                      {userDetails.linked_accounts.map((linked, idx) => (
                        <div key={idx} className="bg-[#1A1A1D] p-3 rounded text-sm">
                          <div className="text-white">{linked.email}</div>
                          <div className="text-[#6B6B6E]">
                            Lien: {linked.link_type === 'shared_ip' ? '📍 Même IP' : '📱 Même Device'} • {linked.link_value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions Admin */}
                <div className="bg-[#222226] p-4 rounded-lg">
                  <h3 className="text-white font-bold mb-3">🎯 Actions Admin</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => updateAdminDecision(userDetails.user.id, 'approved', 'Compte validé manuellement')}
                      className="btn-secondary py-2 text-sm flex items-center justify-center space-x-1"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Marquer Sûr</span>
                    </button>
                    <button
                      onClick={() => updateAdminDecision(userDetails.user.id, 'flagged', 'Compte à surveiller')}
                      className="bg-[#FFB800] hover:bg-[#FFB800]/80 text-black font-bold py-2 rounded-lg text-sm transition-colors"
                    >
                      Surveiller
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Êtes-vous sûr de vouloir désactiver ce compte ?')) {
                          updateAdminDecision(userDetails.user.id, 'banned', 'Compte banni pour fraude');
                        }
                      }}
                      className="bg-[#FF3B5C] hover:bg-[#FF3B5C]/80 text-white font-bold py-2 rounded-lg text-sm transition-colors col-span-2"
                    >
                      Désactiver Compte
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SecurityDashboard;
